import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { TextCell } from '../../../../components/cell/shared/Cells';
import { SUPPORTED_GRADES } from '../../../../constants/shared/GradesConstants';
import Group from './Group';
import UpdateRecommendations from '../UpdateRecommendations';
import {
    NO_GROUPS_MESSAGE,
    UPDATE_RECOMMENDATIONS_TEXT,
    UPDATE_RECOMMENDATION_TEXT_HISTORICAL,
} from '../../../../constants/shared/InstructionConstants';
import './Groups.scss';
import { convertToLowerKebabCase, generateReactKey } from '../../../../utils/shared/commonUtils';
import { BLURB_UPDATE_REC } from '../../../../constants/reading/DemoModeConstants';
import { PERIOD } from '../../../../constants/shared/CommonConstants';

const { EOY, SUMMER } = PERIOD;

const Groups = ({
    checkPointPeriod,
    groups,
    date,
    selectedGrade,
    isCheckPointSaving,
    saveCheckPoint,
    observationsContent,
    activitiesData,
    measureList,
    observationActivitiesMapper,
    Widget,
    groupsMessage,
    staffId,
    lrsTrackingInfo,
    BulkActivitiesWidget,
    isUpdateRecommendationButtonEnabled,
    navigateToLatestDate,
    seeActivitiesToggle,
    downloadAllActivities,
    selectObservation,
    selectPage,
    lrsLanguageConsiderationPdf,
    switchLanguageConsiderationsTabLrs,
    viewLatestUpdateRecommendation,
    sessionId,
    lastLocation,
    isDemoModeOn,
    isGuidedTourOn,
    isSecure,
    SecureResultBanner,
    currentPeriod,
    SummerPeriodBanner,
    isLouisiana,
}) => {
    const buttonText = isUpdateRecommendationButtonEnabled
        ? UPDATE_RECOMMENDATIONS_TEXT
        : UPDATE_RECOMMENDATION_TEXT_HISTORICAL;
    const updateRecommendationsButtonClickHandler = () => {
        if (isUpdateRecommendationButtonEnabled) {
            viewLatestUpdateRecommendation({
                buttonText: convertToLowerKebabCase(buttonText),
                date,
            });
            saveCheckPoint();
        } else {
            navigateToLatestDate({ buttonText: convertToLowerKebabCase(buttonText), date });
        }
    };

    return (
        <div className="groups-container">
            {SecureResultBanner}
            {SummerPeriodBanner}
            <div
                data-tour-step={BLURB_UPDATE_REC}
                className={`blurb ${isUpdateRecommendationButtonEnabled ? '' : 'disabled'}`}
            >
                <TextCell
                    content={isEmpty(groups) && !isSecure ? NO_GROUPS_MESSAGE : groupsMessage}
                    classNames="groups-and-activities-message"
                    isDataCell={false}
                />
                {SUPPORTED_GRADES.includes(selectedGrade) && (
                    <UpdateRecommendations
                        date={date}
                        clickHandler={updateRecommendationsButtonClickHandler}
                        classNames={isUpdateRecommendationButtonEnabled ? '' : 'btn-secondary'}
                        classNamesIcons={
                            isUpdateRecommendationButtonEnabled
                                ? 'icon-amplify-refresh'
                                : 'icon-v2-arrow-next'
                        }
                        buttonText={buttonText}
                        isDisabled={
                            isCheckPointSaving ||
                            (currentPeriod === EOY && isSecure) ||
                            (isLouisiana && currentPeriod === SUMMER)
                        }
                    />
                )}
            </div>
            {groups.map((studentsGroup, index) => {
                const partialPdfName = `${selectedGrade}-${checkPointPeriod}`;

                const bulkActivitiesWidgetProps = {
                    partialPdfName,
                    name: studentsGroup.name,
                    onClick: downloadAllActivities,
                    className: isDemoModeOn
                        ? 'bulk-activities-link in-demo'
                        : 'bulk-activities-link',
                };

                const widget = isEmpty(studentsGroup.widgetData) ? null : (
                    <Widget widgetData={studentsGroup.widgetData} />
                );

                return (
                    <Group
                        key={generateReactKey(index)}
                        groupId={index + 1}
                        {...studentsGroup}
                        content={observationsContent}
                        activitiesData={activitiesData}
                        measureList={measureList}
                        observationActivitiesMapper={observationActivitiesMapper}
                        selectedGrade={selectedGrade}
                        widget={widget}
                        staffId={staffId}
                        lrsTrackingInfo={lrsTrackingInfo}
                        bulkActivitiesWidget={BulkActivitiesWidget}
                        bulkActivitiesWidgetProps={bulkActivitiesWidgetProps}
                        seeActivitiesToggle={seeActivitiesToggle}
                        selectObservation={selectObservation}
                        selectPage={selectPage}
                        lrsLanguageConsiderationPdf={lrsLanguageConsiderationPdf}
                        switchLanguageConsiderationsTabLrs={switchLanguageConsiderationsTabLrs}
                        sessionId={sessionId}
                        lastLocation={lastLocation}
                        isGuidedTourOn={isGuidedTourOn}
                    />
                );
            })}
        </div>
    );
};

Groups.defaultProps = {
    checkPointPeriod: '',
    groups: [],
    date: null,
    selectedGrade: null,
    saveCheckPoint: () => {},
    seeActivitiesToggle: () => {},
    downloadAllActivities: () => {},
    selectObservation: () => {},
    selectPage: () => {},
    navigateToLatestDate: () => {},
    isCheckPointSaving: false,
    observationsContent: [],
    activitiesData: [],
    measureList: [],
    observationActivitiesMapper: {},
    Widget: () => <></>,
    BulkActivitiesWidget: () => <></>,
    groupsMessage: null,
    staffId: null,
    lrsTrackingInfo: {},
    isUpdateRecommendationButtonEnabled: true,
    lrsLanguageConsiderationPdf: () => {},
    switchLanguageConsiderationsTabLrs: () => {},
    viewLatestUpdateRecommendation: () => {},
    sessionId: '',
    lastLocation: {},
    isDemoModeOn: false,
    isGuidedTourOn: false,
    isSecure: false,
    SecureResultBanner: <></>,
    currentPeriod: '',
    SummerPeriodBanner: <></>,
    isLouisiana: false,
};

Groups.propTypes = {
    checkPointPeriod: PropTypes.string,
    groups: PropTypes.array,
    date: PropTypes.string,
    selectedGrade: PropTypes.string,
    saveCheckPoint: PropTypes.func,
    navigateToLatestDate: PropTypes.func,
    isCheckPointSaving: PropTypes.bool,
    observationsContent: PropTypes.array,
    activitiesData: PropTypes.array,
    measureList: PropTypes.array,
    observationActivitiesMapper: PropTypes.object,
    Widget: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    BulkActivitiesWidget: PropTypes.func,
    groupsMessage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    staffId: PropTypes.string,
    lrsTrackingInfo: PropTypes.object,
    isUpdateRecommendationButtonEnabled: PropTypes.bool,
    seeActivitiesToggle: PropTypes.func,
    downloadAllActivities: PropTypes.func,
    selectObservation: PropTypes.func,
    selectPage: PropTypes.func,
    lrsLanguageConsiderationPdf: PropTypes.func,
    switchLanguageConsiderationsTabLrs: PropTypes.func,
    viewLatestUpdateRecommendation: PropTypes.func,
    sessionId: PropTypes.string,
    lastLocation: PropTypes.object,
    isDemoModeOn: PropTypes.bool,
    isGuidedTourOn: PropTypes.bool,
    isSecure: PropTypes.bool,
    SecureResultBanner: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
    currentPeriod: PropTypes.string,
    SummerPeriodBanner: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
    isLouisiana: PropTypes.bool,
};

export default Groups;
